/**
 * Main Application Entry Point
 *
 * Demonstrates integration of:
 * - Bootstrap 5.3 for UI components
 * - SQLocal (SQLite in browser via OPFS)
 * - BroadcastChannel for cross-tab sync
 * - OpenLayers map with ol-ext LayerSwitcher
 * - PWA features (Service Worker, install prompt, offline detection)
 * - Multi-language support (i18n)
 */

// Bootstrap CSS and JS
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Modal, Offcanvas } from 'bootstrap';

// Database module (uses SQLocal directly, BroadcastChannel for tab sync)
import {
  sql,
  dbReady,
  initSchema,
  addLocation,
  getLocations,
  getLocationCount,
  deleteLocation,
  getDatabaseStatus,
  downloadDatabase,
  onDatabaseChange,
  exportToGeoJSON
} from './src/database.js';

// Map component with OpenLayers and ol-ext LayerSwitcher
import { MapView } from './src/components/MapView.js';

// Measurement tools (Circle, Line)
import { MapTools } from './src/components/MapTools.js';

// PWA module (registers Service Worker, handles install/offline)
import { initPWA, isOnline, onOfflineChange } from './src/pwa.js';

// i18n module
import { t, setLanguage, getLanguage, initI18n, onLanguageChange, getCategoryLabel, getSpeechLang } from './src/i18n.js';

// Map instance (global for access across functions)
let mapView = null;
let mapTools = null;

// Current interaction mode: 'addLocation' | 'measureCircle' | 'measureLine'
let currentMode = 'addLocation';

// ============================================================================
// Draggable Bottom Sheet Controller
// ============================================================================

const bottomSheet = {
  el: null,
  backdrop: null,
  handle: null,
  closeBtn: null,
  body: null,
  state: 'peek',     // 'peek' | 'half' | 'full'

  // Snap points as translateY percentage (peek is computed at init)
  snaps: { peek: 95, half: 50, full: 0 },

  // Touch / mouse tracking
  startY: 0,
  startTranslate: 0,
  currentTranslate: 95,
  isDragging: false,
  startTime: 0,

  init() {
    this.el = document.getElementById('bottom-sheet');
    this.backdrop = document.getElementById('bottom-sheet-backdrop');
    this.handle = this.el?.querySelector('.bottom-sheet-handle');
    this.closeBtn = this.el?.querySelector('.bottom-sheet-close');
    this.body = this.el?.querySelector('.bottom-sheet-body');

    if (!this.el || !this.handle) return;

    // Touch events on the handle
    this.handle.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: true });
    this.handle.addEventListener('touchmove', (e) => this._onTouchMove(e), { passive: false });
    this.handle.addEventListener('touchend', () => this._onDragEnd());

    // Mouse events on the handle (desktop)
    this.handle.addEventListener('mousedown', (e) => this._onMouseDown(e));

    // Close button → return to peek (not fully hidden)
    this.closeBtn?.addEventListener('click', () => this.close());

    // Backdrop click to return to peek
    this.backdrop?.addEventListener('click', () => this.close());

    // Escape key to return to peek
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state !== 'peek') this.close();
    });

    // Compute peek snap and show handle immediately
    requestAnimationFrame(() => {
      this._computePeek();
      this.state = 'peek';
      this.currentTranslate = this.snaps.peek;
      this.el.style.transform = `translateY(${this.currentTranslate}%)`;
      this.el.classList.remove('state-open');   // behind dock at peek
      this.el.setAttribute('aria-hidden', 'false');
    });
  },

  /** Open to half-screen (or full) */
  open(snapTo = 'half') {
    this.el.setAttribute('aria-hidden', 'false');
    this._setState(snapTo);
  },

  /** Collapse back to peek (handle visible) */
  close() {
    this._setState('peek');
  },

  /** Toggle between peek ↔ half */
  toggle() {
    if (this.state === 'peek') this.open('half');
    else this.close();
  },

  /**
   * Compute peek translateY so the handle sits just above the dock.
   * Sheet: bottom:0, height = vh−56.  Dock: 68 px from viewport bottom.
   * We want 28 px of handle visible above the dock, rest behind it.
   */
  _computePeek() {
    const h = this._getSheetHeight();
    if (h > 0) {
      const handleHeight = 28;
      const dockHeight = 68;
      this.snaps.peek = ((h - handleHeight - dockHeight) / h) * 100;
    }
  },

  _setState(state) {
    this.state = state;
    this.currentTranslate = this.snaps[state];
    this.el.classList.remove('no-transition');
    this.el.style.transform = `translateY(${this.currentTranslate}%)`;

    if (state === 'peek') {
      this.backdrop?.classList.remove('visible');
      // After slide-down transition finishes, drop z-index behind dock
      const onEnd = () => {
        this.el.removeEventListener('transitionend', onEnd);
        if (this.state === 'peek') this.el.classList.remove('state-open');
      };
      this.el.addEventListener('transitionend', onEnd);
    } else {
      // Immediately raise z-index above dock
      this.el.classList.add('state-open');
      this.backdrop?.classList.add('visible');
    }
  },

  _getSheetHeight() {
    return this.el.getBoundingClientRect().height;
  },

  // --- Touch handling ---

  _onTouchStart(e) {
    this._computePeek();          // recalc in case viewport changed
    this.el.classList.add('state-open');   // above dock while dragging
    this.isDragging = true;
    this.startY = e.touches[0].clientY;
    this.startTranslate = this.currentTranslate;
    this.startTime = Date.now();
    this.el.classList.add('no-transition');
  },

  _onTouchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();

    const deltaY = e.touches[0].clientY - this.startY;
    const sheetHeight = this._getSheetHeight();
    const deltaPercent = (deltaY / sheetHeight) * 100;

    // Clamp between full (0%) and peek
    const maxTranslate = this.snaps.peek + 2;   // tiny overscroll tolerance
    this.currentTranslate = Math.max(0, Math.min(maxTranslate, this.startTranslate + deltaPercent));
    this.el.style.transform = `translateY(${this.currentTranslate}%)`;

    // Update backdrop opacity (0 at peek, 1 at full)
    const peekPct = this.snaps.peek;
    const progress = Math.max(0, 1 - (this.currentTranslate / peekPct));
    if (this.backdrop) {
      this.backdrop.style.opacity = progress;
      this.backdrop.classList.toggle('visible', progress > 0.02);
    }
  },

  _onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.el.classList.remove('no-transition');
    if (this.backdrop) this.backdrop.style.opacity = '';

    // Velocity-based snapping
    const elapsed = Date.now() - this.startTime;
    const deltaPercent = this.currentTranslate - this.startTranslate;
    const velocity = deltaPercent / elapsed;   // %/ms

    if (velocity > 0.12) {
      // Fast swipe down → peek
      this._setState('peek');
    } else if (velocity < -0.12) {
      // Fast swipe up → full
      this._setState('full');
    } else {
      this._snapToNearest(this.currentTranslate);
    }
  },

  _snapToNearest(pct) {
    let closestState = 'peek';
    let minDist = Infinity;

    for (const [name, val] of Object.entries(this.snaps)) {
      const dist = Math.abs(pct - val);
      if (dist < minDist) {
        minDist = dist;
        closestState = name;
      }
    }
    this._setState(closestState);
  },

  // --- Mouse handling (desktop) ---

  _onMouseDown(e) {
    this._computePeek();
    this.el.classList.add('state-open');   // above dock while dragging
    this.isDragging = true;
    this.startY = e.clientY;
    this.startTranslate = this.currentTranslate;
    this.startTime = Date.now();
    this.el.classList.add('no-transition');

    const onMouseMove = (e) => {
      if (!this.isDragging) return;
      const deltaY = e.clientY - this.startY;
      const sheetHeight = this._getSheetHeight();
      const deltaPercent = (deltaY / sheetHeight) * 100;
      const maxTranslate = this.snaps.peek + 2;
      this.currentTranslate = Math.max(0, Math.min(maxTranslate, this.startTranslate + deltaPercent));
      this.el.style.transform = `translateY(${this.currentTranslate}%)`;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      this._onDragEnd();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }
};

// ============================================================================
// Application Initialization
// ============================================================================

async function initApp() {
  console.log('[App] Initializing...');

  // 1. Initialize i18n
  initI18n();
  initLanguageSwitcher();

  // 2. Initialize PWA features (Service Worker, install prompt, offline detection)
  await initPWA({
    installButton: '#install-btn',
    offlineIndicator: '#offline-indicator',
    autoRegisterSW: true
  });

  // 3. Initialize the map
  mapView = new MapView('map', {
    center: [-1.5, 7.5],  // Ghana
    zoom: 7,
    basemap: 'osm'
  });

  // 3b. Initialize map measurement tools (Circle, Line)
  mapTools = new MapTools(mapView.getMap());

  // Log measurement results
  mapTools.onMeasureComplete((result) => {
    console.log('[MapTools] Measurement complete:', result);
  });

  // Handle "Navigate to" POI requests (OSRM routing)
  mapView.onNavigateTo((poi) => {
    navigateToOSRM(poi);
  });

  // Set up map click handler immediately after map creation
  mapView.onClick((lon, lat, feature, evt) => {
    console.log('[App] Map clicked:', lon, lat, feature);

    // Show tap ripple at click position
    showMapTapRipple(evt.pixel);

    // Dismiss popups on any click
    mapView.hidePoiActionPopup();
    mapView.hideCirclePoiPopup();

    // ── Clicked a measurement circle → show POI count popup ──────
    if (feature && feature.get('_layerType') === 'measure_circle') {
      const circleGeom = feature.getGeometry();
      const poiSource = mapView.getPoiSource();
      const counts = countPOIsInCircle(circleGeom, poiSource);
      const radius = feature.get('_radius');
      mapView.showCirclePoiPopup(evt.coordinate, counts, radius);
      return;
    }

    // Only handle location-related clicks in addLocation mode
    if (currentMode !== 'addLocation') {
      console.log('[App] Not in addLocation mode, skipping popup');
      return;
    }

    if (feature) {
      // Check if this is a cluster feature (from Cluster source)
      const clusterMembers = feature.get('features');
      if (clusterMembers) {
        if (clusterMembers.length === 1) {
          // Single-POI cluster → show action popup with the original feature
          mapView.showPoiActionPopup(clusterMembers[0], evt.coordinate);
        } else {
          // Multi-POI cluster → zoom in to expand
          mapView.zoomToCluster(feature);
        }
      } else if (feature.get('featureType') === 'poi') {
        // Direct POI feature (defensive fallback)
        mapView.showPoiActionPopup(feature, evt.coordinate);
      } else {
        // Clicked on user marker - select it and show details
        mapView.selectMarker(feature);
        showLocationDetails(feature);
      }
    } else {
      // Clicked on empty space - show add location popup at click position
      mapView.clearSelection();
      mapView.showAddLocationPopup(evt.coordinate);
    }
  });

  // Set up handler for the map add location popup form
  mapView.onAddLocation(async (data) => {
    console.log('[App] Add location from map popup:', data);
    try {
      const result = await addLocation(data.name, data.lon, data.lat, {
        description: data.description || null,
        category: data.category || 'default'
      });
      console.log('[App] Location added:', data.name, 'id:', result.id);

      await loadLocations();

      // Zoom to the new location on the map
      mapView?.zoomTo(data.lon, data.lat, 14);

      // Select the new marker
      if (result.id) {
        mapView?.selectMarker(result.id);
      }

      showSuccess(t('locations.addedSuccess'));

    } catch (error) {
      console.error('[App] Failed to add location:', error);
      showError(t('locations.addFailed') + ': ' + error.message);
    }
  });

  // 4. Initialize database (non-fatal — POI and map work without it)
  let dbOk = false;
  try {
    console.log('[App] Initializing database...');

    await initSchema();
    console.log('[App] Database ready');

    const status = await getDatabaseStatus();
    console.log('[App] Database status:', status);
    dbOk = true;

  } catch (error) {
    console.error('[App] Database initialization failed:', error);
    showError(t('error.dbInit'));
  }

  // 5. Initialize UI
  initUI();

  // 6. Load initial data and display on map (only if DB is ready)
  if (dbOk) {
    await loadLocations();

    // 7. Listen for database changes from other tabs
    onDatabaseChange((change) => {
      console.log('[App] Database change:', change);
      if (change.table === 'locations' && !change.local) {
        loadLocations();
      }
    });
  }

  // 8. Set up offline handling
  onOfflineChange((offline) => {
    if (offline) {
      console.log('[App] Working offline - data will sync when back online');
    } else {
      console.log('[App] Back online - syncing data...');
      syncData();
    }
  });

  // 9. Load POI layer from PostGIS API, then wire filter chips
  loadPOI().then(() => initPoiFilterChips());

  // 10. Re-render locations list when language changes
  onLanguageChange(() => {
    if (dbOk) loadLocations();
    // Update MapView popup/form translations
    mapView?.updateI18n();
  });

  console.log('[App] Initialized successfully');
}

// ============================================================================
// Language Switcher
// ============================================================================

function initLanguageSwitcher() {
  const select = document.getElementById('language-select');
  if (!select) return;

  // Set initial value
  select.value = getLanguage();

  select.addEventListener('change', (e) => {
    setLanguage(e.target.value);
  });

  // Keep select in sync when language changes programmatically
  onLanguageChange((lang) => {
    select.value = lang;
  });
}

// ============================================================================
// UI Initialization
// ============================================================================

function initUI() {
  // Export button
  const exportBtn = document.getElementById('export-btn');
  if (exportBtn) {
    exportBtn.addEventListener('click', handleExport);
  }

  // GeoJSON Export button
  const exportGeoJSONBtn = document.getElementById('exportGeoJSON-btn');
  if (exportGeoJSONBtn) {
    exportGeoJSONBtn.addEventListener('click', handleExportGeoJSON);
  }

  // Status button
  const statusBtn = document.getElementById('status-btn');
  if (statusBtn) {
    statusBtn.addEventListener('click', handleShowStatus);
  }

  // Fit to markers button
  const fitBtn = document.getElementById('fit-btn');
  if (fitBtn) {
    fitBtn.addEventListener('click', () => mapView?.fitToMarkers());
  }

  // ============================================
  // Mode Selector & Measurement Tools (Bottom Dock)
  // ============================================

  const fabBtn = document.getElementById('fab-add-location');
  const measureCircleBtn = document.getElementById('dock-btn-measure-circle');
  const measureLineBtn = document.getElementById('dock-btn-measure-line');
  const clearBtn = document.getElementById('dock-btn-clear');
  const locateBtn = document.getElementById('dock-btn-locate');
  const settingsBtn = document.getElementById('dock-btn-settings');

  // Dock measurement mode buttons (mutually exclusive .active)
  const dockModeButtons = [measureCircleBtn, measureLineBtn];

  // Helper to set active mode and update button + FAB states
  const setMode = (mode, activeBtn) => {
    console.log('[setMode] Changing mode from', currentMode, 'to', mode);
    currentMode = mode;

    // Update dock button active states
    dockModeButtons.forEach(btn => {
      if (btn) btn.classList.toggle('active', btn === activeBtn);
    });

    // FAB: dim when a measurement tool is active, bright otherwise
    fabBtn?.classList.toggle('dimmed', mode !== 'addLocation');

    // Deactivate any measurement tool when switching modes
    mapTools?.deactivate();

    // Hide popups when switching modes
    if (mode !== 'addLocation') {
      mapView?.hideAddLocationPopup();
    }
    mapView?.hideCirclePoiPopup();

    // Disable hover popups while a measurement tool is active
    if (mapView) {
      mapView.hoverPopupDisabled = mode.startsWith('measure');
      if (mapView.hoverPopupDisabled) mapView.hidePopup();
    }

    // Activate the appropriate tool for the new mode
    switch (mode) {
      case 'measureCircle':
        mapTools?.startCircleMeasure();
        break;
      case 'measureLine':
        mapTools?.startLineMeasure();
        break;
      // addLocation mode doesn't need tool activation
    }
  };

  // FAB — Add Location (primary CTA)
  if (fabBtn) {
    fabBtn.addEventListener('click', () => {
      setMode('addLocation', null);
    });
  }

  // Circle measurement button
  if (measureCircleBtn) {
    measureCircleBtn.addEventListener('click', () => {
      if (currentMode === 'measureCircle') {
        // Toggle off - return to addLocation mode
        setMode('addLocation', null);
      } else {
        setMode('measureCircle', measureCircleBtn);
      }
    });
  }

  // Line measurement button
  if (measureLineBtn) {
    measureLineBtn.addEventListener('click', () => {
      if (currentMode === 'measureLine') {
        setMode('addLocation', null);
      } else {
        setMode('measureLine', measureLineBtn);
      }
    });
  }

  // Clear button - clears measurements but stays in current mode
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      mapTools?.clearMeasurements();
      mapView?.hideCirclePoiPopup();
      // If in a measurement mode, restart the tool
      if (currentMode.startsWith('measure')) {
        mapTools?.deactivate();
        switch (currentMode) {
          case 'measureCircle':
            mapTools?.startCircleMeasure();
            break;
          case 'measureLine':
            mapTools?.startLineMeasure();
            break;
        }
      }
    });
  }

  // Locate button - trigger geolocation
  if (locateBtn) {
    locateBtn.addEventListener('click', () => {
      // Trigger the ol-ext GeolocationButton
      if (mapView?.geolocationButton) {
        mapView.geolocationButton.element.querySelector('button')?.click();
      }
    });
  }

  // Settings button - restore settings content and toggle bottom sheet
  if (settingsBtn) {
    settingsBtn.addEventListener('click', () => {
      restoreSettingsSheet();
      bottomSheet.toggle();
    });
  }

  // Initialize the draggable bottom sheet (starts at peek = handle visible)
  bottomSheet.init();

  // Wire fieldwork mode toggle in the settings sheet
  wireFieldworkToggle();

  // Wire font size stepper and apply saved size
  applyFontSize(getFontSizeStep());
  wireFontSizeControl();

  // Voice input (Web Speech API) — progressive enhancement
  initVoiceInput();
}

// ============================================================================
// Location Handlers
// ============================================================================

async function handleAddLocation(event) {
  event.preventDefault();

  const form = event.target;
  const formData = new FormData(form);

  const name = formData.get('name');
  const longitude = parseFloat(formData.get('longitude'));
  const latitude = parseFloat(formData.get('latitude'));
  const description = formData.get('description') || null;
  const category = formData.get('category') || 'default';

  if (!name || isNaN(longitude) || isNaN(latitude)) {
    showError(t('error.requiredFields'));
    return;
  }

  try {
    const result = await addLocation(name, longitude, latitude, { description, category });
    console.log('[App] Location added:', name, 'id:', result.id);

    form.reset();
    await loadLocations();

    mapView?.zoomTo(longitude, latitude, 14);

    if (result.id) {
      mapView?.selectMarker(result.id);
    }

    showSuccess(t('locations.addedSuccess'));

  } catch (error) {
    console.error('[App] Failed to add location:', error);
    showError(t('locations.addFailed') + ': ' + error.message);
  }
}

async function loadLocations() {
  try {
    console.log('[App] Loading locations...');
    const locations = await getLocations();
    console.log('[App] Locations loaded:', locations);

    // Update the list
    renderLocations(locations);

    // Update the map markers
    if (mapView) {
      mapView.clearMarkers();
      if (locations.length > 0) {
        mapView.addMarkers(locations);
        console.log('[App] Added', locations.length, 'markers to map');
      }
    }

    // Update count display
    const countEl = document.getElementById('location-count');
    if (countEl) {
      countEl.textContent = locations.length;
    }

  } catch (error) {
    console.error('[App] Failed to load locations:', error);
  }
}

/**
 * Show details for a selected location
 */
function showLocationDetails(feature) {
  const name = feature.get('name');
  const description = feature.get('description');
  const category = feature.get('category');
  const lon = feature.get('lon') || feature.get('longitude');
  const lat = feature.get('lat') || feature.get('latitude');

  console.log('[App] Selected location:', { name, description, category, lon, lat });
}

function renderLocations(locations) {
  const container = document.getElementById('locations-list');
  if (!container) return;

  // Also update mobile count
  const mobileCount = document.getElementById('location-count-mobile');
  if (mobileCount) {
    mobileCount.textContent = locations.length;
  }

  if (locations.length === 0) {
    container.innerHTML = `
      <div class="text-center py-4 px-3">
        <div style="font-size:3rem; margin-bottom:8px; opacity:0.6;">🗺️</div>
        <p class="mb-1 fw-semibold" style="color:var(--bw-primary);">
          ${escapeHtml(t('locations.empty'))}
        </p>
        <p class="text-muted small mb-3">${escapeHtml(t('locations.emptyHint'))}</p>
        <button type="button" class="btn btn-accent btn-sm" id="empty-state-locate-btn">
          <i class="bi bi-geo-alt me-1"></i>${escapeHtml(t('dock.locate'))}
        </button>
      </div>
    `;
    // Wire the CTA to trigger geolocation + close the offcanvas
    const locateBtn = document.getElementById('empty-state-locate-btn');
    if (locateBtn) {
      locateBtn.addEventListener('click', () => {
        // Close the right offcanvas
        const offcanvasEl = document.getElementById('offcanvasRight');
        if (offcanvasEl) {
          const instance = Offcanvas.getInstance(offcanvasEl);
          if (instance) instance.hide();
        }
        // Trigger geolocation
        mapView?.geolocationButton?.element?.querySelector('button')?.click();
      });
    }
    return;
  }

  // Category emoji mapping
  const categoryEmojis = {
    'water': '💧',
    'school': '🏫',
    'health': '🏥',
    'market': '🏪',
    'hotel': '🏨',
    'restaurant': '🍽',
    'default': '📍',
    'other': '📌'
  };

  container.innerHTML = locations.map(loc => {
    const emoji = categoryEmojis[loc.category] || '📍';
    const categoryLabel = getCategoryLabel(loc.category);
    return `
      <a href="#" class="list-group-item list-group-item-action location-item py-2"
         data-id="${loc.id}" data-lon="${loc.longitude}" data-lat="${loc.latitude}">
        <div class="d-flex w-100 justify-content-between align-items-start">
          <div class="location-item-info">
            <h6 class="mb-1">${emoji} ${escapeHtml(loc.name)}</h6>
            <small class="text-muted font-monospace">${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}</small>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge badge-${loc.category}">${escapeHtml(categoryLabel)}</span>
            <button type="button" class="location-delete-btn" data-id="${loc.id}"
                    aria-label="Delete location" title="Delete">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
        ${loc.description ? `<small class="text-secondary d-block mt-1">${escapeHtml(loc.description)}</small>` : ''}
      </a>
    `;
  }).join('');

  // Add click handlers to zoom to location
  container.querySelectorAll('.location-item').forEach(item => {
    item.addEventListener('click', (e) => {
      // Ignore clicks on the delete button
      if (e.target.closest('.location-delete-btn')) return;

      e.preventDefault();
      const lon = parseFloat(item.dataset.lon);
      const lat = parseFloat(item.dataset.lat);
      const id = parseInt(item.dataset.id);

      mapView?.zoomTo(lon, lat, 14);
      mapView?.selectMarker(id);
    });
  });

  // Wire delete buttons
  container.querySelectorAll('.location-delete-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const id = parseInt(btn.dataset.id);
      const item = btn.closest('.location-item');
      const name = item?.querySelector('h6')?.textContent?.trim() || '';

      if (!confirm(`Delete "${name}"?`)) return;

      try {
        await deleteLocation(id);
        mapView?.removeMarker(id);
        await loadLocations();
        showSuccess(t('locations.deleted'));
      } catch (err) {
        console.error('[App] Delete location error:', err);
        showError(t('locations.deleteError'));
      }
    });
  });
}

// ============================================================================
// Export Handler
// ============================================================================

async function handleExport() {
  try {
    await downloadDatabase('bisawofa-backup.sqlite3');
    showSuccess(t('locations.export') + ' - OK');
  } catch (error) {
    console.error('[App] Export failed:', error);
    showError(t('error.export') + ': ' + error.message);
  }
}

// Export as GeoJSON file
async function handleExportGeoJSON() {
  try {
	  const geojson = await exportToGeoJSON();

	  // Download as file
	  const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
	  const url = URL.createObjectURL(blob);
	  const a = document.createElement('a');
	  a.href = url;
	  a.download = 'locations.geojson';
	  a.click();
	  URL.revokeObjectURL(url);

		showSuccess(`${t('locations.exportGeoJSON')} - ${geojson.features.length} location(s)`);
	}catch (error) {
    console.error('[App] GeoJSON Export failed:', error);
    showError(t('error.export') + ': ' + error.message);
  }
}

// ============================================================================
// Status Handler
// ============================================================================

async function handleShowStatus() {
  try {
    const status = await getDatabaseStatus();

    const statusContent = document.getElementById('status-content');
    if (statusContent) {
      statusContent.innerHTML = `
        <table class="table table-sm table-borderless mb-0">
          <tbody>
            <tr>
              <td class="fw-semibold">${escapeHtml(t('status.ready'))}:</td>
              <td><span class="badge ${status.ready ? 'bg-success' : 'bg-danger'}">${status.ready ? t('status.yes') : t('status.no')}</span></td>
            </tr>
            <tr>
              <td class="fw-semibold">${escapeHtml(t('status.online'))}:</td>
              <td><span class="badge ${isOnline() ? 'bg-success' : 'bg-warning'}">${isOnline() ? t('status.yes') : t('status.offline')}</span></td>
            </tr>
            <tr>
              <td class="fw-semibold">${escapeHtml(t('status.database'))}:</td>
              <td><code>${status.databasePath || 'N/A'}</code></td>
            </tr>
            <tr>
              <td class="fw-semibold">${escapeHtml(t('status.tables'))}:</td>
              <td>${status.tables.map(tbl => `<span class="badge bg-secondary me-1">${tbl}</span>`).join('')}</td>
            </tr>
            <tr>
              <td class="fw-semibold">${escapeHtml(t('status.locations'))}:</td>
              <td><span class="badge bg-primary">${status.locationCount}</span></td>
            </tr>
          </tbody>
        </table>
      `;
    }

    const statusModal = new Modal(document.getElementById('statusModal'));
    statusModal.show();

  } catch (error) {
    console.error('[App] Failed to get status:', error);
    showError(t('error.export'));
  }
}

// ============================================================================
// POI Layer (PostGIS / OSM data)
// ============================================================================

/**
 * Load POI features from the PHP REST API.
 * Fetches GeoJSON from /api/poi.php and adds to the POI layer.
 */
async function loadPOI() {
  if (!mapView) return;

  const toast = document.getElementById('poi-loading-toast');
  const toastText = toast?.querySelector('.poi-loading-text');

  try {
    console.log('[App] Loading POI data...');

    // Show loading toast
    if (toast) {
      toast.classList.remove('done');
      if (toastText) toastText.textContent = t('general.loading');
      toast.classList.add('visible');
    }

    const count = await mapView.loadPOI('/api/poi.php');
    console.log(`[App] POI loaded: ${count} features`);

    // Show success in toast, then fade out
    if (toast) {
      toast.classList.add('done');
      if (toastText) toastText.textContent = `${count} POI`;
      setTimeout(() => toast.classList.remove('visible'), 2000);
    }
  } catch (error) {
    // POI loading is non-critical - log but don't show error to user
    console.warn('[App] POI loading failed (API may not be running):', error.message);

    // Hide toast on failure
    if (toast) {
      toast.classList.remove('visible');
    }
  }
}

// ============================================================================
// POI Category Filter Chips
// ============================================================================

/**
 * Wire up the POI category filter chip buttons.
 * Each chip toggles its amenity type on/off via MapView.setPoiFilter().
 */
function initPoiFilterChips() {
  if (!mapView) return;

  const strip = document.getElementById('poi-filter-strip');
  if (!strip) return;

  const chips = strip.querySelectorAll('.poi-chip');
  if (!chips.length) return;

  // Build initial active set from chips that start with .active
  const activeAmenities = new Set();
  chips.forEach(chip => {
    const amenity = chip.dataset.amenity;
    if (chip.classList.contains('active')) activeAmenities.add(amenity);
  });

  // Show feature counts on each chip
  const counts = mapView.getPoiCounts();
  chips.forEach(chip => {
    const amenity = chip.dataset.amenity;
    const count = counts[amenity] || 0;
    if (count > 0) {
      const badge = document.createElement('span');
      badge.className = 'poi-chip-count';
      badge.textContent = count.toLocaleString();
      chip.appendChild(badge);
    }
  });

  // Click handler — toggle amenity on/off
  chips.forEach(chip => {
    chip.addEventListener('click', () => {
      const amenity = chip.dataset.amenity;

      if (activeAmenities.has(amenity)) {
        // Don't allow turning off ALL chips — at least one must stay on
        if (activeAmenities.size <= 1) return;
        activeAmenities.delete(amenity);
        chip.classList.remove('active');
      } else {
        activeAmenities.add(amenity);
        chip.classList.add('active');
      }

      mapView.setPoiFilter(new Set(activeAmenities));
      console.log('[POI Filter] Active:', [...activeAmenities]);
    });
  });

  console.log('[POI Filter] Initialized with', activeAmenities.size, 'categories');

  // --- "Find Nearest" button (inside the filter strip) ---
  const nearestBtn = document.getElementById('btn-find-nearest');
  if (nearestBtn) {
    nearestBtn.addEventListener('click', () => findNearestPOI());
  }
}

// ============================================================================
// Find Nearest POI
// ============================================================================

/**
 * Get the user's geolocation, compute distances to visible POI,
 * and show the sorted results in the bottom sheet.
 */
function findNearestPOI() {
  if (!mapView) return;

  const sheetBody = bottomSheet.body;
  if (!sheetBody) return;

  // Update bottom sheet header
  const sheetLabel = document.getElementById('bottomSheetLabel');
  if (sheetLabel) {
    sheetLabel.innerHTML = '<i class="bi bi-signpost-split me-2"></i>Nearest POI';
  }

  // Show loading state and open sheet
  sheetBody.innerHTML = `
    <div class="nearest-empty">
      <div class="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
      <div>${escapeHtml(t('general.loading'))}</div>
    </div>
  `;
  bottomSheet.open('half');

  // Get geolocation
  if (!navigator.geolocation) {
    sheetBody.innerHTML = `
      <div class="nearest-empty">
        <i class="bi bi-exclamation-triangle"></i>
        Geolocation is not supported by your browser.
      </div>
    `;
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const lon = position.coords.longitude;
      const lat = position.coords.latitude;

      console.log('[Nearest] User position:', lon, lat);

      const results = mapView.findNearestPOI(lon, lat, { limit: 20 });

      if (results.length === 0) {
        sheetBody.innerHTML = `
          <div class="nearest-empty">
            <i class="bi bi-geo-alt"></i>
            No POI found. Try enabling more categories.
          </div>
        `;
        return;
      }

      // Render results list
      sheetBody.innerHTML = `
        <div style="font-size: 12px; color: var(--bw-text-muted); margin-bottom: 10px;">
          ${results.length} results from your location
        </div>
        <ul class="nearest-results">
          ${results.map((r, i) => {
            const distStr = r.distance < 1000
              ? `${Math.round(r.distance)} m`
              : `${(r.distance / 1000).toFixed(1)} km`;
            const amenityLabel = r.amenity.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const displayName = r.name || amenityLabel;
            return `
              <li class="nearest-item" data-lon="${r.lon}" data-lat="${r.lat}">
                <span class="nearest-item-emoji">${r.emoji}</span>
                <div class="nearest-item-info">
                  <div class="nearest-item-name">${escapeHtml(displayName)}</div>
                  <div class="nearest-item-detail">${escapeHtml(amenityLabel)}${r.brand ? ' · ' + escapeHtml(r.brand) : ''}</div>
                </div>
                <span class="nearest-item-dist">${distStr}</span>
              </li>
            `;
          }).join('')}
        </ul>
      `;

      // Wire click → zoom to POI
      sheetBody.querySelectorAll('.nearest-item').forEach(item => {
        item.addEventListener('click', () => {
          const pLon = parseFloat(item.dataset.lon);
          const pLat = parseFloat(item.dataset.lat);
          mapView.zoomTo(pLon, pLat, 16);
          bottomSheet.close();
        });
      });
    },
    (error) => {
      console.warn('[Nearest] Geolocation error:', error.message);
      sheetBody.innerHTML = `
        <div class="nearest-empty">
          <i class="bi bi-exclamation-triangle"></i>
          Could not get your location.<br>
          <small>${escapeHtml(error.message)}</small>
        </div>
      `;
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

// ============================================================================
// OSRM Routing / "Navigate to" POI
// ============================================================================

/**
 * Get the user's geolocation, request a route from OSRM, and display it.
 * @param {{lon:number, lat:number, name:string, amenity:string, emoji:string}} poi
 */
function navigateToOSRM(poi) {
  if (!mapView) return;

  const routeInfo = document.getElementById('route-info');

  // Show loading state
  if (routeInfo) {
    routeInfo.innerHTML = `
      <div class="route-info-content">
        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
        <span>Finding route to ${escapeHtml(poi.emoji + ' ' + (poi.name || 'POI'))}…</span>
      </div>
    `;
    routeInfo.classList.add('visible');
  }

  if (!navigator.geolocation) {
    showError('Geolocation is not supported by your browser.');
    if (routeInfo) routeInfo.classList.remove('visible');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      const fromLon = position.coords.longitude;
      const fromLat = position.coords.latitude;

      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${fromLon},${fromLat};${poi.lon},${poi.lat}?overview=full&geometries=geojson`;
        console.log('[OSRM] Requesting route:', url);

        const response = await fetch(url);
        const data = await response.json();

        if (data.code !== 'Ok' || !data.routes?.length) {
          throw new Error(data.message || 'No route found');
        }

        const route = data.routes[0];
        const coords = route.geometry.coordinates;
        const distance = route.legs[0].distance;  // metres
        const duration = route.legs[0].duration;  // seconds

        console.log('[OSRM] Route found:', (distance / 1000).toFixed(1), 'km,', Math.ceil(duration / 60), 'min');

        // Draw the route line on the map
        mapView.showRoute(coords);

        // Show route info bar
        showRouteInfo(poi, distance, duration);

      } catch (error) {
        console.error('[OSRM] Routing failed:', error);
        showError('Could not find route: ' + error.message);
        if (routeInfo) routeInfo.classList.remove('visible');
      }
    },
    (error) => {
      console.warn('[OSRM] Geolocation error:', error.message);
      showError('Could not get your location: ' + error.message);
      if (routeInfo) routeInfo.classList.remove('visible');
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

/**
 * Show the route info bar above the dock with distance, duration, and clear button.
 */
function showRouteInfo(poi, distance, duration) {
  const routeInfo = document.getElementById('route-info');
  if (!routeInfo) return;

  const distStr = distance < 1000
    ? `${Math.round(distance)} m`
    : `${(distance / 1000).toFixed(1)} km`;

  const durMin = Math.ceil(duration / 60);
  const durStr = durMin < 60
    ? `${durMin} min`
    : `${Math.floor(durMin / 60)}h ${durMin % 60}m`;

  routeInfo.innerHTML = `
    <div class="route-info-content">
      <span class="route-info-dest">${poi.emoji} ${escapeHtml(poi.name || 'POI')}</span>
      <span class="route-info-stats">
        <i class="bi bi-signpost-2"></i> ${distStr}
        <span class="route-info-dot">·</span>
        <i class="bi bi-clock"></i> ${durStr}
      </span>
    </div>
    <button class="route-info-close" type="button" title="Clear route">
      <i class="bi bi-x-lg"></i>
    </button>
  `;
  routeInfo.classList.add('visible');

  routeInfo.querySelector('.route-info-close').addEventListener('click', () => {
    mapView.clearRoute();
    routeInfo.classList.remove('visible');
  });
}

// ============================================================================
// Fieldwork Mode (High Contrast)
// ============================================================================

const FIELDWORK_STORAGE_KEY = 'bisawofa-fieldwork';

/**
 * Wire the fieldwork mode toggle switch in the settings sheet.
 * Reads/writes localStorage and toggles `body.fieldwork-mode`.
 */
function wireFieldworkToggle() {
  const toggle = document.getElementById('fieldwork-toggle');
  if (!toggle) return;

  // Restore saved state
  const saved = localStorage.getItem(FIELDWORK_STORAGE_KEY) === '1';
  toggle.checked = saved;
  applyFieldworkMode(saved);

  toggle.addEventListener('change', () => {
    const enabled = toggle.checked;
    localStorage.setItem(FIELDWORK_STORAGE_KEY, enabled ? '1' : '0');
    applyFieldworkMode(enabled);
  });
}

/**
 * Apply or remove fieldwork mode on the body and update theme-color.
 */
function applyFieldworkMode(enabled) {
  document.body.classList.toggle('fieldwork-mode', enabled);

  // Update the theme-color meta tag so the browser chrome matches
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.content = enabled ? '#000000' : '#8B008B';
  }
}

// ============================================================================
// Font Size Control
// ============================================================================

const FONTSIZE_STORAGE_KEY = 'bisawofa-fontsize';
const FONTSIZE_LABELS = ['S', 'M', 'L', 'XL'];
const FONTSIZE_DEFAULT = 1;   // index 1 = 16px (browser default)

/**
 * Wire the A / A+ font-size stepper buttons in the settings sheet.
 */
function wireFontSizeControl() {
  const downBtn = document.getElementById('font-size-down');
  const upBtn   = document.getElementById('font-size-up');
  const label   = document.getElementById('font-size-label');
  if (!downBtn || !upBtn || !label) return;

  let step = getFontSizeStep();
  updateFontSizeUI(step, downBtn, upBtn, label);

  downBtn.addEventListener('click', () => {
    if (step <= 0) return;
    step--;
    applyFontSize(step);
    updateFontSizeUI(step, downBtn, upBtn, label);
  });

  upBtn.addEventListener('click', () => {
    if (step >= FONTSIZE_LABELS.length - 1) return;
    step++;
    applyFontSize(step);
    updateFontSizeUI(step, downBtn, upBtn, label);
  });
}

/**
 * Read the saved font-size step from localStorage.
 */
function getFontSizeStep() {
  const saved = parseInt(localStorage.getItem(FONTSIZE_STORAGE_KEY), 10);
  return isNaN(saved) ? FONTSIZE_DEFAULT : Math.max(0, Math.min(saved, FONTSIZE_LABELS.length - 1));
}

/**
 * Apply a font-size step by setting a class on <html>.
 * Step 1 is the default (16px) → no class needed.
 */
function applyFontSize(step) {
  const html = document.documentElement;
  // Remove all font-size-* classes
  html.classList.remove('font-size-0', 'font-size-2', 'font-size-3');
  // Add the class for this step (skip step 1 = default)
  if (step !== 1) {
    html.classList.add(`font-size-${step}`);
  }
  localStorage.setItem(FONTSIZE_STORAGE_KEY, String(step));
}

/**
 * Update the stepper UI (label text, disabled states).
 */
function updateFontSizeUI(step, downBtn, upBtn, label) {
  label.textContent = FONTSIZE_LABELS[step];
  downBtn.disabled = step <= 0;
  upBtn.disabled = step >= FONTSIZE_LABELS.length - 1;
}

// ============================================================================
// Voice Input  (Web Speech API)
// ============================================================================

/**
 * Initialise voice input for all `.voice-mic-btn` buttons.
 *
 * Progressive enhancement:
 * - Checks for SpeechRecognition API support.
 * - Adds `speech-available` class to <body> so the CSS un-hides mic buttons.
 * - Creates a shared voice-toast element for user feedback.
 * - Delegates click events on `.voice-mic-btn` via document-level listener
 *   so dynamically-created popups (OpenLayers overlays) are handled too.
 */
function initVoiceInput() {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return;               // API not supported — mic buttons stay hidden

  document.body.classList.add('speech-available');

  // ── shared toast element ──────────────────────────────────────────────
  let toast = document.querySelector('.voice-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'voice-toast';
    document.body.appendChild(toast);
  }

  let hideTimer = null;
  function showToast(msg, duration = 2000) {
    clearTimeout(hideTimer);
    toast.textContent = msg;
    toast.classList.add('visible');
    hideTimer = setTimeout(() => toast.classList.remove('visible'), duration);
  }

  // ── track active recognition so we can abort on a second tap ──────────
  let activeRecognition = null;
  let activeBtn = null;

  function stopActiveRecognition() {
    if (activeRecognition) {
      try { activeRecognition.abort(); } catch (_) { /* ignore */ }
      activeRecognition = null;
    }
    if (activeBtn) {
      activeBtn.classList.remove('listening');
      activeBtn = null;
    }
  }

  // ── delegated click handler ───────────────────────────────────────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.voice-mic-btn');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    // If this button is already listening → stop
    if (btn === activeBtn) {
      stopActiveRecognition();
      toast.classList.remove('visible');
      return;
    }

    // Stop any other active session first
    stopActiveRecognition();

    // Find the target input/textarea
    const targetId = btn.getAttribute('data-voice-target');
    const target = targetId && document.getElementById(targetId);
    if (!target) return;

    // ── start recognition ─────────────────────────────────────────────
    const recognition = new SpeechRecognition();
    recognition.lang        = getSpeechLang();
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous  = false;

    activeRecognition = recognition;
    activeBtn = btn;
    btn.classList.add('listening');
    showToast(t('voice.listening'), 8000);       // long timeout while listening

    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript?.trim();
      if (transcript) {
        // Append to existing value (user may have typed something already)
        const prev = target.value;
        target.value = prev ? prev + ' ' + transcript : transcript;
        // Trigger an input event so any listeners (validation, etc.) fire
        target.dispatchEvent(new Event('input', { bubbles: true }));
        showToast(transcript, 2500);
      } else {
        showToast(t('voice.noResult'), 2500);
      }
    };

    recognition.onerror = (event) => {
      console.warn('[Voice] SpeechRecognition error:', event.error);
      if (event.error === 'no-speech') {
        showToast(t('voice.noResult'), 2500);
      } else if (event.error === 'aborted') {
        // User-initiated abort — no feedback needed
      } else {
        showToast(t('voice.error'), 2500);
      }
    };

    recognition.onend = () => {
      if (activeBtn === btn) {
        btn.classList.remove('listening');
        activeBtn = null;
        activeRecognition = null;
      }
    };

    try {
      recognition.start();
    } catch (err) {
      console.warn('[Voice] Could not start recognition:', err);
      showToast(t('voice.error'), 2500);
      stopActiveRecognition();
    }
  });
}

/**
 * Restore the settings content inside the bottom sheet body.
 * Called when the Settings dock button is tapped after Nearest POI
 * (or other content) replaced the sheet body.
 */
function restoreSettingsSheet() {
  const sheetBody = document.getElementById('bottom-sheet-body');
  if (!sheetBody) return;

  // If settings content is already showing, nothing to do
  if (sheetBody.querySelector('#settings-content')) return;

  // Restore header
  const sheetLabel = document.getElementById('bottomSheetLabel');
  if (sheetLabel) {
    sheetLabel.innerHTML = `<i class="bi bi-sliders me-2"></i><span data-i18n="nav.bottomPanel">${escapeHtml(t('dock.settings'))}</span>`;
  }

  // Restore settings HTML
  const isChecked = localStorage.getItem(FIELDWORK_STORAGE_KEY) === '1';
  const fontStep = getFontSizeStep();
  sheetBody.innerHTML = `
    <div id="settings-content">
      <div class="settings-item">
        <div class="settings-item-info">
          <span class="settings-item-icon">☀️</span>
          <div>
            <div class="settings-item-label" data-i18n="settings.fieldwork">${escapeHtml(t('settings.fieldwork'))}</div>
            <div class="settings-item-desc" data-i18n="settings.fieldworkDesc">${escapeHtml(t('settings.fieldworkDesc'))}</div>
          </div>
        </div>
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" role="switch" id="fieldwork-toggle" ${isChecked ? 'checked' : ''}>
        </div>
      </div>
      <div class="settings-item">
        <div class="settings-item-info">
          <span class="settings-item-icon">🔤</span>
          <div>
            <div class="settings-item-label" data-i18n="settings.fontSize">${escapeHtml(t('settings.fontSize'))}</div>
            <div class="settings-item-desc" data-i18n="settings.fontSizeDesc">${escapeHtml(t('settings.fontSizeDesc'))}</div>
          </div>
        </div>
        <div class="font-size-stepper">
          <button type="button" id="font-size-down" aria-label="Decrease font size">A</button>
          <span class="font-size-step" id="font-size-label">${FONTSIZE_LABELS[fontStep]}</span>
          <button type="button" id="font-size-up" aria-label="Increase font size">A</button>
        </div>
      </div>
    </div>
  `;

  // Re-wire controls since we recreated the DOM elements
  wireFieldworkToggle();
  wireFontSizeControl();
}

// ============================================================================
// Circle ↔ POI intersection counting
// ============================================================================

/**
 * Count fuel, bus_station, and taxi POI features inside a circle geometry.
 *
 * Uses extent pre-filter + precise intersectsCoordinate for efficiency.
 *
 * @param {import('ol/geom/Circle').default} circleGeom  Circle in EPSG:3857
 * @param {import('ol/source/Vector').default} poiSource  POI VectorSource
 * @returns {{ fuel: number, bus_station: number, taxi: number }}
 */
function countPOIsInCircle(circleGeom, poiSource) {
  const targets = ['fuel', 'bus_station', 'taxi'];
  const counts = { fuel: 0, bus_station: 0, taxi: 0 };
  const extent = circleGeom.getExtent();

  poiSource.forEachFeatureInExtent(extent, (feature) => {
    const amenity = feature.get('amenity');
    if (!targets.includes(amenity)) return;

    const coord = feature.getGeometry().getCoordinates();
    if (circleGeom.intersectsCoordinate(coord)) {
      counts[amenity]++;
    }
  });

  return counts;
}

// ============================================================================
// Map tap ripple
// ============================================================================

/**
 * Show a brief ripple animation at the given pixel position on the map.
 */
function showMapTapRipple(pixel) {
  const mapEl = document.getElementById('map');
  if (!mapEl) return;

  const ripple = document.createElement('div');
  ripple.className = 'map-tap-ripple';
  ripple.style.left = pixel[0] + 'px';
  ripple.style.top = pixel[1] + 'px';
  mapEl.appendChild(ripple);

  // Trigger animation on next frame so the browser registers the initial state
  requestAnimationFrame(() => ripple.classList.add('animate'));

  // Remove after animation completes
  ripple.addEventListener('animationend', () => ripple.remove());
}

// ============================================================================
// Sync (placeholder - implement based on your backend)
// ============================================================================

async function syncData() {
  if (!isOnline()) {
    console.log('[App] Cannot sync - offline');
    return;
  }

  // TODO: Implement sync with your backend
  console.log('[App] Sync placeholder - implement based on your backend');
}

// ============================================================================
// Utilities
// ============================================================================

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function showError(message) {
  const el = document.getElementById('error-message');
  if (el) {
    el.querySelector('.message-text').textContent = message;
    el.classList.remove('d-none');

    setTimeout(() => {
      el.classList.add('d-none');
    }, 5000);
  } else {
    console.error(message);
  }
}

function showSuccess(message) {
  const el = document.getElementById('success-message');
  if (el) {
    el.querySelector('.message-text').textContent = message;
    el.classList.remove('d-none');

    setTimeout(() => {
      el.classList.add('d-none');
    }, 3000);
  } else {
    console.log(message);
  }
}

// ============================================================================
// Start Application
// ============================================================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
